/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useApp } from "../../contexts/AppContext";
import { BaobabMotif } from "./BaobabTree";
import { MapPin, Sprout, ShieldCheck, WifiOff, ChevronRight, Users, Activity, GitFork } from "lucide-react";

export const CENTRE_KEY = "bc_centre_me";

export function useCentre() {
  const [me, setMe] = useState(() => { try { return JSON.parse(localStorage.getItem(CENTRE_KEY) || "null"); } catch { return null; } });
  const load = () => api.get("/network/me").then(({ data }) => { setMe(data); localStorage.setItem(CENTRE_KEY, JSON.stringify(data)); }).catch(() => null);
  useEffect(() => { load(); window.addEventListener("bc-centre-changed", load); return () => window.removeEventListener("bc-centre-changed", load); }, []);
  return me;
}

/** En-tête « Mon Centre Bombacare » : nom, localisation, badge réseau, 3 indicateurs. */
export function CentreHeader({ me, compact = false }) {
  const navigate = useNavigate();
  if (!me) return null;
  const lieu = [me.ville, me.pays].filter(Boolean).join(", ");
  return (
    <section data-testid="centre-header" className="bb-hero bb-grain relative rounded-3xl p-5 md:p-6 bb-rise">
      <BaobabMotif />
      <div className="relative flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-bold">Mon Centre Bombacare</div>
          <h1 data-testid="centre-name" className="font-[Fraunces] text-2xl md:text-3xl font-bold leading-tight mt-1">{me.nom}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {lieu && <span data-testid="centre-location" className="inline-flex items-center gap-1 text-[13px] text-white/85"><MapPin size={14} className="text-[#D4AF5A]" /> {lieu}</span>}
            <span data-testid="centre-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF5A] text-[#0A1426] text-[11px] font-bold"><Sprout size={13} /> Branche du réseau Bombacare</span>
            {me.parrain && <span data-testid="centre-parrain" className="text-[11px] text-white/70">rattachée à {me.parrain.nom}</span>}
          </div>
        </div>
        {!compact && (
          <div className="grid grid-cols-3 gap-2 md:w-[360px]">
            {[["centre-kpi-patients", Users, me.patients, "patients"], ["centre-kpi-assiduite", Activity, `${me.assiduite_pct}%`, "assiduité 7 j"], ["centre-kpi-filleuls", GitFork, me.filleuls, "parrainé(s)"]].map(([id, I, v, l]) => (
              <div key={id} data-testid={id} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-3">
                <I size={16} className="text-[#D4AF5A]" /><div className="font-[Fraunces] text-2xl font-bold leading-none mt-1">{v}</div><div className="text-[10px] text-white/70 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        )}
        <button data-testid="centre-open-network" onClick={() => navigate("/pro/reseau")} className="h-11 px-4 rounded-full bg-white text-[#0A1426] text-[13px] font-bold inline-flex items-center gap-1.5 shrink-0 w-fit">Voir le baobab <ChevronRight size={16} /></button>
      </div>
    </section>
  );
}

/** Bandeau résilience : l'offline-first comme vertu de marque. */
export function ResilienceBanner({ compact = false }) {
  const { online, pending } = useApp();
  return (
    <div data-testid="resilience-banner" className={`rounded-2xl bg-[#0A1426] text-white border border-[#2BA3A0]/30 flex items-center gap-3 ${compact ? "px-3 py-2" : "px-5 py-3"}`}>
      <span className="w-9 h-9 rounded-xl bg-[#2BA3A0]/20 grid place-items-center shrink-0">{online ? <ShieldCheck size={18} className="text-[#D4AF5A]" /> : <WifiOff size={18} className="text-[#D4AF5A]" />}</span>
      <div className="min-w-0">
        <div className={`font-[Fraunces] font-bold leading-tight ${compact ? "text-[12px]" : "text-[14px]"}`}>Conçu pour tenir même hors ligne, comme le baobab en saison sèche.</div>
        {!compact && <div className="text-[11px] text-white/70">{online ? "Réseau disponible — vos données sont à jour." : `Hors-ligne — ${pending || 0} action(s) en attente, tout continue de fonctionner.`}</div>}
      </div>
    </div>
  );
}
